import {
  decodePricingEdgeCode,
  PRICING_TEST_V2_FLAG_KEY,
} from '@listening/api-contract/pricing-experiment';
import {
  clearManualFlagOverride,
  clearLocalFlag,
  setLocalFlag,
  subscribeManualFlagOverrides,
  useFlag,
  readManualOverrideFlags,
  setManualFlagOverride,
} from '@listening/frontend/analytics/client';
import type { EdgeBucketedFlagKey } from '@listening/frontend/analytics/define-route-analytics';
import { ClientFlags } from '@listening/frontend/analytics/flags/client';
import type { FeatureFlagDef } from '@listening/frontend/analytics/flags/types';
import { Button } from '@listening/frontend/components/radix/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@listening/frontend/components/radix/popover';
import { ClientOnly } from '@tanstack/react-router';
import { useState, useSyncExternalStore } from 'react';
import { clientEnv } from '@/env';
import * as m from '@/paraglide/messages';
import { Predicate } from 'effect';

function Experiment({ name }: { name: EdgeBucketedFlagKey }) {
  const definition: FeatureFlagDef = ClientFlags[name];
  const override = useSyncExternalStore(
    subscribeManualFlagOverrides,
    () => readManualOverrideFlags()[definition.key] ?? '',
    () => '',
  );
  return (
    <label className='grid gap-2 text-[11px] text-(--pricing-panel-muted)'>
      <span>{definition.label ?? definition.key}</span>
      <select
        aria-label={m.pricing_experiment_override({ name: definition.key })}
        className='w-full rounded-md border border-(--pricing-panel-field-border) bg-(--pricing-panel-field) p-2.5 text-xs text-(--pricing-panel-text)'
        value={Predicate.isString(override) ? override : ''}
        onChange={(event) => {
          const variant = event.target.value;
          if (variant) setManualFlagOverride(definition.key, variant);
          else clearManualFlagOverride(definition.key);
        }}
      >
        <option value=''>{m.pricing_experiment_automatic()}</option>
        {definition.variants.map((variant) => (
          <option key={variant} value={variant}>
            {definition.variantLabels?.[variant] ??
              (definition.key === PRICING_TEST_V2_FLAG_KEY
                ? decodePricingEdgeCode(variant)
                : null) ??
              variant}
          </option>
        ))}
      </select>
    </label>
  );
}

function CountrySimulation() {
  const { value } = useFlag('simulateFreeCountry');
  return (
    <label className='grid gap-2 text-[11px] text-(--pricing-panel-muted)'>
      <span>{m.pricing_country_simulation()}</span>
      <select
        aria-label={m.pricing_country_simulation()}
        className='w-full rounded-md border border-(--pricing-panel-field-border) bg-(--pricing-panel-field) p-2.5 text-xs text-(--pricing-panel-text)'
        value={value === 'true' ? 'free' : 'automatic'}
        onChange={(event) => {
          const key = ClientFlags.simulateFreeCountry.key;
          if (event.target.value === 'free') setLocalFlag(key, 'true');
          else clearLocalFlag(key);
        }}
      >
        <option value='automatic'>{m.pricing_country_automatic()}</option>
        <option value='free'>{m.pricing_country_free()}</option>
      </select>
    </label>
  );
}

export function PricingExperimentSelector({
  experimentNames,
}: {
  experimentNames: ReadonlyArray<EdgeBucketedFlagKey>;
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  if (clientEnv.mode === 'production') return null;
  const pricingExperiments = experimentNames.filter((name) =>
    ClientFlags[name].key.split('-').includes('pricing'),
  );
  return (
    <ClientOnly>
      <Popover open={panelOpen} onOpenChange={setPanelOpen}>
        <PopoverTrigger asChild>
          <Button className='pricing-theme fixed right-5 bottom-4.25 z-50 h-auto rounded-[9px] border border-(--pricing-panel-border) bg-(--pricing-ink) px-3.75 py-2.75 font-(family-name:--pricing-font) text-xs leading-normal font-normal text-(--pricing-paper) shadow-md hover:bg-(--pricing-panel)'>
            {m.pricing_options()}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          aria-label={m.pricing_experiments()}
          align='end'
          side='top'
          sideOffset={12}
          className='pricing-theme max-h-[calc(100svh-95px)] w-[min(380px,calc(100vw-32px))] overflow-y-auto rounded-[14px] border-(--pricing-panel-border) bg-(--pricing-panel) p-5.5 font-(family-name:--pricing-font) text-xs leading-normal text-(--pricing-panel-text) shadow-xl'
        >
          <div className='mb-5 flex items-center justify-between'>
            <h2 className='text-sm font-semibold'>{m.pricing_experiments()}</h2>
            <button
              type='button'
              aria-label={m.common_close()}
              onClick={() => setPanelOpen(false)}
              className='cursor-pointer border-0 bg-transparent text-[22px] leading-none text-(--pricing-panel-muted)'
            >
              ×
            </button>
          </div>
          <div className='grid gap-4.5'>
            <CountrySimulation />
            {pricingExperiments.length === 0 ? (
              <p>{m.pricing_no_experiments()}</p>
            ) : (
              pricingExperiments.map((name) => (
                <Experiment key={name} name={name} />
              ))
            )}
          </div>
          <p className='mt-3.25 text-[11px] leading-[1.6] text-(--pricing-panel-muted)'>
            {m.pricing_experiment_notice()}
          </p>
        </PopoverContent>
      </Popover>
    </ClientOnly>
  );
}
